/* eslint-disable react/jsx-no-bind */
import { Link, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import AnnotationsListRenderer from "~/components/build/Show/components/AnnotationsListRenderer";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { SubNavBar, SubNavBarRow } from "~/components/build/Show/components/SubNavLink";
import Count from "~/components/build/Show/components/Count";
import BuildAnnotations from "~/components/build/Show/pages/annotations/BuildAnnotations";
import JobAnnotations from "~/components/build/Show/pages/annotations/JobAnnotations";
import { AnnotationsTab } from "~/components/build/Show/lib/types";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";

const TAB_LABELS: Record<AnnotationsTab, string> = {
  [AnnotationsTab.Build]: "Build",
  [AnnotationsTab.Jobs]: "Jobs",
};

const DEFAULT_TAB = AnnotationsTab.Build;

function isValidTab(value: string | null | undefined): value is AnnotationsTab {
  return Object.values(AnnotationsTab).includes(value as AnnotationsTab);
}

function AnnotationsTabLink({
  tab,
  isActive,
  count,
}: {
  tab: AnnotationsTab;
  isActive: boolean;
  count?: number;
}) {
  const [searchParams] = useSearchParams();
  const nextParams = new URLSearchParams(searchParams);

  if (tab === DEFAULT_TAB) {
    nextParams.delete("annotations_tab");
  } else {
    nextParams.set("annotations_tab", tab);
  }

  const search = nextParams.toString();

  return (
    <Link
      to={search ? `?${search}` : ""}
      className={twMerge(
        classNames(
          "self-center px-2 py-1 text-sm",
          "flex items-center justify-center rounded-md border border-gray-500",
          "text-charcoal-400 no-underline hover:text-current hover:no-underline focus:text-current focus:no-underline",
          "transition-colors duration-75 hover:border-slate-400 hover:bg-white",
          {
            "text-charcoal-700 shadow-xs border-slate-500 bg-white": isActive,
          },
        ),
      )}
    >
      {TAB_LABELS[tab]}
      {count != null && count > 0 && (
        <span className="ml-1">
          <Count>{count}</Count>
        </span>
      )}
    </Link>
  );
}

const LegacyAnnotations = () => {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();

  return (
    <div onClick={navigateClickHandler} className="flex flex-1 flex-col">
      <AnnotationsListRenderer
        params={{
          buildSlug: `${build.account.slug}/${build.project.slug}/${build.number}`,
        }}
        organization={build.account.slug}
        pipeline={build.project.slug}
        number={build.number}
        filterIssues={false}
        hasFailedJobs={false}
      />
    </div>
  );
};

function AnnotationsContent({ activeTab }: { activeTab: AnnotationsTab }) {
  if (!Features.BuildPageAnnotationsNavigation) {
    return <LegacyAnnotations />;
  }

  switch (activeTab) {
    case AnnotationsTab.Build:
      return <BuildAnnotations />;
    case AnnotationsTab.Jobs:
      return <JobAnnotations />;
  }
}

export default function AnnotationsPage() {
  const { annotationsTab } = useQueryParams();
  const activeTab = isValidTab(annotationsTab) ? annotationsTab : DEFAULT_TAB;
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);

  if (!Features.BuildJobAnnotationsTab) {
    return (
      <div className="relative min-w-0 flex-auto">
        <div className="absolute flex h-full w-full overflow-y-auto">
          <div className="relative flex w-full flex-1 flex-col gap-2 p-3">
            <LegacyAnnotations />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="AnnotationsPage" className="flex min-w-0 flex-auto flex-col">
      {Features.BuildPageAnnotationsNavigation && (
        <SubNavBar>
          <SubNavBarRow>
            {Object.values(AnnotationsTab).map((tab) => (
              <AnnotationsTabLink
                key={tab}
                tab={tab}
                isActive={tab === activeTab}
                count={annotationCounts[tab]}
              />
            ))}
          </SubNavBarRow>
        </SubNavBar>
      )}

      <div className="relative flex-auto">
        <div className="absolute flex h-full w-full overflow-y-auto">
          <div className="relative flex w-full flex-1 flex-col gap-2 p-3">
            <AnnotationsContent activeTab={activeTab} />
          </div>
        </div>
      </div>
    </div>
  );
}
